<script setup lang="ts">
// ================================
// LA RANGÉE « RAPPORT ET DEMANDE » (contrat de l'interface v1.2 §7.4 ;
// maquette 2c jugée le 18/10/2026 ; décisions D3 a, D16 a, D17 a, A4, A9 ;
// DEV-2311, lot 4b, DEV-2419).
//
// Ce que le courtier doit vivre : tout se lit et se copie ici, dans cet
// ordre. Avec une démarche : le tableau « À reporter dans la demande », le
// courriel (aperçu modifiable, « Copier le courriel »), « Ensuite » ; puis,
// pour tous les états, « Le rapport » (« Télécharger le rapport » en niveau
// 2, « Aperçu du rapport » en lien), « Limites de l'étude » repliée et
// « Résumé pour votre client », lu avant « Copier le résumé ». Rien à
// demander : aucune ligne qui le redit (A4), la rangée s'ouvre sur le rapport
// et le résumé se présente déplié.
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import OptimTurpeV2Demande from "./OptimTurpeV2Demande.vue"
import OptimTurpeV2Remise from "./OptimTurpeV2Remise.vue"
import OptimTurpeV2Section from "./OptimTurpeV2Section.vue"
import {
    NIVEAU_2,
    NIVEAU_3,
} from "@modules/courtage/utils/optim-turpe-v2.boutons"
import {
    resumeEnBlocs,
    texteDuResume,
    type CarteDeTete,
} from "@modules/courtage/utils/optim-turpe-v2.carte"
import { exclusionsDuSegment } from "@modules/courtage/utils/optim-turpe-v2.demande"
import {
    enteteLimites,
    enteteResume,
} from "@modules/courtage/utils/optim-turpe-v2.entetes"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            leRapport: "Le rapport",
            telecharger: "Télécharger le rapport",
            composition: "Composition…",
            apercu: "Aperçu du rapport",
            reessayer: "Réessayer",
            enCours:
                "Composition du document, quelques secondes. Vous pouvez continuer à lire la page.",
            // E10 : aucune invitation à réessayer, la fonction est absente.
            indisponible: "Le rapport n'est pas disponible pour le moment.",
        },
    },
})

type Props = {
    etude: OptimTurpeV2Etude
    /** La carte de tête affichée : le résumé reprend ses mots (§7.5). */
    carte: CarteDeTete | null
    /** Le point de livraison complet (complet dans le courriel). */
    pdl?: string | null
    site?: string | null
    /** « 05/10/2026 » : la date de calcul de l'étude. */
    date?: string | null
    signataire?: string | null
    societe?: string | null
    valeursParDefaut?: string[]
    courrielOuvert?: boolean
    resumeOuvert?: boolean
    limitesOuvertes?: boolean
    /** Le rapport se propose-t-il (sa route répond, étude réelle) ? */
    rapportProposable?: boolean
    rapportIndisponible?: boolean
    rapportEnCours?: boolean
    /** Le message d'un téléchargement en échec, ou `null`. */
    rapportErreur?: string | null
    /** Étude d'exemple (story, démonstration) : les gestes du rapport sont inactifs. */
    demonstration?: boolean
}
const props = withDefaults(defineProps<Props>(), {
    pdl: null,
    site: null,
    date: null,
    signataire: null,
    societe: null,
    valeursParDefaut: () => [],
    courrielOuvert: false,
    resumeOuvert: false,
    limitesOuvertes: false,
    rapportProposable: false,
    rapportIndisponible: false,
    rapportEnCours: false,
    rapportErreur: null,
    demonstration: false,
})
const emit = defineEmits<{
    "update:courrielOuvert": [ouvert: boolean]
    "update:resumeOuvert": [ouvert: boolean]
    "update:limitesOuvertes": [ouvert: boolean]
    telecharger: []
    apercu: []
    /** « Corriger » d'une condition : le champ de la puissance de raccordement. */
    corriger: []
}>()

/** D17 : étude saisie ou scénario retenu contre le conseil. */
const getPasLaRecommandation = computed(
    () =>
        props.carte?.etat === "vos_puissances" ||
        props.carte?.etat === "scenario_non_recommande"
)
const getResume = computed(() => resumeEnBlocs(props.carte))
const getEnteteResume = computed(() =>
    enteteResume(getResume.value ? texteDuResume(getResume.value) : null)
)
const getEnteteLimites = computed(() => enteteLimites())
const getExclusions = computed(() =>
    exclusionsDuSegment(props.etude.compteur?.segment ?? null)
)
</script>

<template>
    <div
        class="flex flex-col gap-4 text-sm"
        data-testid="optim-turpe-v2-rapport-et-demande"
    >
        <OptimTurpeV2Demande
            :etude="etude"
            :pdl="pdl"
            :site="site"
            :date="date"
            :signataire="signataire"
            :societe="societe"
            :valeurs-par-defaut="valeursParDefaut"
            :pas-la-recommandation="getPasLaRecommandation"
            :courriel-ouvert="courrielOuvert"
            @update:courriel-ouvert="emit('update:courrielOuvert', $event)"
            @corriger="emit('corriger')"
        />

        <div
            class="flex flex-col gap-2"
            data-testid="optim-turpe-v2-le-rapport"
        >
            <h3 class="text-sm font-medium text-primary-800">
                {{ t("leRapport") }}
            </h3>
            <BaseMessage
                v-if="rapportErreur"
                severity="error"
                size="small"
                data-testid="optim-turpe-v2-pdf-erreur"
            >
                {{ rapportErreur }}
            </BaseMessage>
            <div
                v-if="!rapportIndisponible"
                class="flex flex-wrap items-center gap-3"
            >
                <BaseButton
                    v-if="rapportProposable"
                    v-bind="NIVEAU_2"
                    :label="
                        rapportEnCours ? t('composition') : t('telecharger')
                    "
                    icon="pi pi-download"
                    :disabled="demonstration"
                    :loading="rapportEnCours"
                    data-testid="optim-turpe-v2-pdf"
                    @click="emit('telecharger')"
                />
                <BaseButton
                    v-if="rapportProposable"
                    v-bind="NIVEAU_3"
                    :label="t('apercu')"
                    :disabled="demonstration || rapportEnCours"
                    data-testid="optim-turpe-v2-pdf-apercu"
                    @click="emit('apercu')"
                />
                <!-- DEV-2164, cas 3 : un échec propose de réessayer. -->
                <BaseButton
                    v-if="rapportProposable && rapportErreur && !rapportEnCours"
                    v-bind="NIVEAU_2"
                    :label="t('reessayer')"
                    icon="pi pi-refresh"
                    data-testid="optim-turpe-v2-pdf-retenter"
                    @click="emit('telecharger')"
                />
                <span
                    v-if="rapportEnCours"
                    class="text-sm text-surface-700"
                    data-testid="optim-turpe-v2-pdf-en-cours"
                    >{{ t("enCours") }}</span
                >
            </div>
            <p v-else class="text-sm text-surface-700">
                {{ t("indisponible") }}
            </p>
        </div>

        <!-- « Limites de l'étude » (Q11), repliée, sur tout état. -->
        <OptimTurpeV2Section
            id="acc-limites"
            :entete="getEnteteLimites"
            :rang="2"
            :ouvert="limitesOuvertes"
            @update:ouvert="emit('update:limitesOuvertes', $event)"
        >
            <ul
                class="flex list-disc flex-col gap-1 pl-5 text-sm text-primary-600"
            >
                <li
                    v-for="exclusion in getExclusions"
                    :key="exclusion"
                    data-testid="optim-turpe-v2-exclusion"
                >
                    {{ exclusion }}
                </li>
            </ul>
        </OptimTurpeV2Section>

        <!-- « Résumé pour votre client » (Q40, D16) : lu avant d'être copié. -->
        <OptimTurpeV2Section
            v-if="getResume"
            id="acc-remise"
            :entete="getEnteteResume"
            :rang="2"
            :ouvert="resumeOuvert"
            @update:ouvert="emit('update:resumeOuvert', $event)"
        >
            <OptimTurpeV2Remise :resume="getResume" />
        </OptimTurpeV2Section>
    </div>
</template>
